import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowLeft, Cookie } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import { applyLocalizedCanonical } from "../lib/seoMeta";

export default function CookiePolicyPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => { applyLocalizedCanonical("/cookies"); }, []);
  useEffect(() => { document.title = `${t("legal.cookies")} · MachinePilot`; }, [t, i18n.language]);

  const k = (key) => `legalPages.cookies.${key}`;
  const rows = (key) => t(k(key), { returnObjects: true }) || [];

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <BrandIcon size={32} />
            <div className="font-display font-bold text-slate-100 group-hover:text-blue-400">MachinePilot</div>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1"><ArrowLeft size={14}/> Home</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="cookies-page">
        <div className="mb-8">
          <div className="label-tech text-amber-400 mb-2">Cookie</div>
          <h1 className="font-display font-bold text-4xl text-slate-100 mb-2 flex items-center gap-3">
            <Cookie size={32} className="text-amber-400"/> {t("legal.cookies")}
          </h1>
          <p className="text-sm text-slate-500">{t("legalPages.updated", { date: t("legalPages.dateValue") })}</p>
        </div>

        <div className="mb-6 p-5 rounded-lg border border-slate-800 bg-slate-900/40 text-sm text-slate-300">
          <Trans
            i18nKey={k("intro")}
            components={{
              set: <Link to="/settings" className="text-amber-400 underline"/>,
              btn: (
                <button
                  onClick={() => window.dispatchEvent(new Event("open-cookie-prefs"))}
                  className="text-amber-400 underline hover:text-amber-300"
                  data-testid="reopen-cookie-banner"
                />
              ),
            }}
          />
        </div>

        <Sec title={t(k("necessaryTitle"))}>
          <Table rows={rows("necessaryRows")} headers={[t(k("thName")), t(k("thProvider")), t(k("thDuration")), t(k("thPurpose"))]}/>
        </Sec>

        <Sec title={t(k("prefsTitle"))}>
          <Table rows={rows("prefsRows")} headers={[t(k("thName")), t(k("thProvider")), t(k("thDuration")), t(k("thPurpose"))]}/>
        </Sec>

        <Sec title={t(k("analyticsTitle"))}>
          <Table rows={rows("analyticsRows")} headers={[t(k("thName")), t(k("thProvider")), t(k("thDuration")), t(k("thPurpose"))]}/>
        </Sec>

        <Sec title={t(k("marketingTitle"))}>
          <p>{t(k("marketingBody"))}</p>
        </Sec>

        <div className="mt-8 flex gap-3 text-sm text-slate-400">
          <Link to="/privacy" className="hover:text-amber-400">{t("legal.privacy")}</Link>
          <Link to="/terms" className="hover:text-amber-400">{t("legal.terms")}</Link>
        </div>
      </main>
    </div>
  );
}

function Sec({ title, children }) {
  return (
    <section className="mb-6 p-5 rounded-lg border border-slate-800 bg-slate-900/40">
      <h2 className="font-display font-semibold text-slate-100 mb-3 text-lg">{title}</h2>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Table({ rows, headers }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left label-tech text-slate-500">
          <tr>
            <th className="py-2 pr-3">{headers[0]}</th>
            <th className="py-2 pr-3">{headers[1]}</th>
            <th className="py-2 pr-3">{headers[2]}</th>
            <th className="py-2">{headers[3]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]} className="border-t border-slate-800">
              <td className="py-2 pr-3 font-mono-data text-amber-300">{r[0]}</td>
              <td className="py-2 pr-3 text-slate-400">{r[1]}</td>
              <td className="py-2 pr-3 text-slate-400">{r[2]}</td>
              <td className="py-2 text-slate-300">{r[3]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
